import React, { useCallback, useMemo, type Ref, type MouseEvent } from 'react';
import { Box, xcss } from '@atlaskit/primitives';
import { asyncInlineEditStatusModal } from '@atlassian/jira-inline-edit-status-modal/entrypoint.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { defaultModalProps } from '@atlassian/jira-status-config-common/src/constants.tsx';
import {
	ISSUE_VIEW_SOURCE,
	type ConfigureSource,
	type Status,
} from '@atlassian/jira-status-config-common/src/types.tsx';
import { ModalEntryPointPressableTrigger } from '@atlassian/jira-modal-entry-point-pressable-trigger/src/ModalEntryPointPressableTrigger.tsx';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { editStatusExperience } from '../../../experiences.tsx';
import { messages } from './messages.tsx';

export type EditStatusOptionProps = {
	projectId: string;
	projectKey: string;
	issueTypeId: string;
	statusNames?: string[];
	status: Status;
	statuses: Status[];
	onClick: () => void;
	onClose: () => void;
	onStatusUpdated?: () => Promise<void>;
	buttonRef: Ref<HTMLButtonElement>;
	source?: ConfigureSource;
};

export const EditStatusOption = ({
	projectId,
	projectKey,
	issueTypeId,
	statusNames = [],
	status,
	statuses,
	onClick,
	onClose,
	onStatusUpdated,
	buttonRef,
	source,
}: EditStatusOptionProps) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleClick = useCallback(
		(_e: MouseEvent<HTMLElement>) => {
			onClick();
			fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'editStatusButton');
			// Because we are triggering the click manually for keyboard users
			// this handler becomes the source-of-truth handler for both keyboard users and mouse users
			// However, we need to stopPropagation from here to prevent double modal for mouse users
			// When mouse user clicks the button, if we don't stop propagation here, handleChange
			// will be called which will trigger click manually again and cause double modal rendering
			_e.stopPropagation();
		},
		[onClick, createAnalyticsEvent],
	);

	const onModalClose = useCallback(() => {
		onClose();
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'closed',
				actionSubject: 'inlineEditStatusModal',
			}),
		);
	}, [createAnalyticsEvent, onClose]);

	const onMouseDown = () => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
			}),
			'editStatusButton',
		);
	};

	const entryPointProps = useMemo(
		() => ({
			status,
			statuses,
			statusNames,
			projectId,
			projectKey,
			onClose: onModalClose,
			isNewControllerEnabled: true,
			issueTypeId,
			editStatusExperience,
			source: source || ISSUE_VIEW_SOURCE,
			onStatusUpdated,
			shouldShowStatusSelect: true,
		}),
		[
			statusNames,
			projectId,
			projectKey,
			issueTypeId,
			source,
			status,
			statuses,
			onStatusUpdated,
			onModalClose,
		],
	);

	return (
		<ModalEntryPointPressableTrigger
			entryPoint={asyncInlineEditStatusModal}
			entryPointProps={entryPointProps}
			interactionName="edit-status-modal"
			modalProps={defaultModalProps}
			useInternalModal
			forwardedRef={buttonRef}
		>
			{({ ref }) => (
				<Box
					role="button"
					xcss={optionStyles}
					onClick={handleClick}
					data-show-on-hover
					ref={ref}
					onMouseDown={(event: MouseEvent<HTMLElement>) => {
						if (event.button === 0) {
							// Left mouse button was clicked
							onMouseDown();
						}
					}}
				>
					{formatMessage(messages.editStatus)}
				</Box>
			)}
		</ModalEntryPointPressableTrigger>
	);
};

const optionStyles = xcss({
	height: '100%',
	width: '100%',
	backgroundColor: 'color.background.neutral.subtle',
	alignItems: 'center',
	textAlign: 'left',
	fontWeight: 'font.weight.medium',
});
